import { Component, computed, effect, inject, input, output, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  IFormsProvider,
  LFOfflineOrderSourceType,
  LiveForm,
  OrderData,
  OrderState,
  ResourceState,
  StyleHelper
} from '@lfw/roesberg.liveforms.core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { concatMap, EMPTY, forkJoin, from, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ProjectsService } from '../../../projects/projects.service';
import { OpenInNewTabDirective } from '../../../route/open-in-new-tab.directive';
import { ConfirmDialogComponent } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { SelectionArguments } from '../../../shared/models/selection-arguments';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { MenuService } from '../../../shell/menu.service';
import { OfflineFormsClient } from '../../offline-orders/offline-forms.client';
import { OfflineFormsProvider } from '../../offline-orders/offline-forms.provider';
import { OrdersService } from '../../orders.service';
import { OnlineFormsProvider } from '../online-forms.provider';
import { OnlineOrdersListActionsComponent } from '../online-order-list-actions/online-order-list-actions.component';

@Component({
  selector: 'lfw-online-order-list-item',
  templateUrl: './online-order-list-item.component.html',
  styleUrls: ['./online-order-list-item.component.scss'],
  providers: [{ provide: IFormsProvider, useClass: OnlineFormsProvider }, OfflineFormsProvider],
  imports: [
    RouterModule,
    MatIcon,
    MatButtonModule,
    OpenInNewTabDirective,
    MatTooltipModule,
    MatMenuModule,
    TranslatePipe,
    OnlineOrdersListActionsComponent
  ]
})
export class OnlineOrderListItemComponent {
  readonly order = input.required<OrderData>();
  readonly projectSelected = output<SelectionArguments>();
  readonly refreshOrders = output<void>();

  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly offlineFormsClient = inject(OfflineFormsClient);
  private readonly projectService = inject(ProjectsService);
  private readonly orderService = inject(OrdersService);
  private readonly formService = inject(IFormsProvider<LiveForm>);
  private readonly offlineFormService = inject(OfflineFormsProvider);
  private readonly errorHandling = inject(ErrorHandlerService);
  private readonly menuService = inject(MenuService);

  private readonly offlineOrders = toSignal(this.offlineFormsClient.offlineOrders$, { initialValue: {} });

  readonly isAvailableOffline = computed(() => this.order().id in this.offlineOrders());
  readonly isConflicted = computed(
    () => this.isAvailableOffline() && this.order().orderState.state !== OrderState.CheckedOut
  );

  // Set while we check this order out ourselves. The offline copy is written before the refreshed list
  // arrives, so until then `order()` still reports the pre-checkout state and judging it would flag a
  // conflict against our own checkout. Plain field, so changing it does not itself trigger the effect.
  private isCheckingOut = false;

  constructor() {
    effect(() => {
      const orderId = this.order().id;
      const isCheckedOut = this.order().orderState.state === OrderState.CheckedOut;

      if (!this.isAvailableOffline()) return; // ← Guard: only relevant orders!

      // Fresh data has arrived once the list agrees the order is checked out.
      if (isCheckedOut) {
        this.isCheckingOut = false;
      } else if (this.isCheckingOut) {
        return;
      }

      const conflicted = this.isConflicted();
      untracked(() => this.offlineFormsClient.setOrderConflicted(orderId, conflicted));
    });
  }

  get userCanCancel(): boolean {
    return this.projectService.currentProjectUserCanCancelOrder;
  }

  getOrderStateText(): string {
    return this.order().orderState.text ?? '';
  }

  getSubStateText(): string {
    return this.order().orderSubState?.text ?? '';
  }

  getScenarioText(): string {
    return this.order().scenario?.name ?? '';
  }

  getOrderStateStyle(): string {
    return this.order().orderState.getStyle();
  }

  getSubStateStyle(): string | undefined {
    return this.order().orderSubState?.getStyle();
  }

  getScenarioStyle(): string {
    return StyleHelper.getColorStyle(this.order().scenario?.color ?? '', '000000') ?? '';
  }

  checkoutOrder(): void {
    const conflictedGuard$ = this.isConflicted()
      ? this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              title: 'ORDERS.CONFLICT_OVERWRITE_TITLE',
              message: 'ORDERS.CONFLICT_OVERWRITE_MESSAGE'
            },
            closeOnNavigation: true,
            disableClose: true
          })
          .afterClosed()
          .pipe(
            take(1),
            switchMap((confirmed: boolean) => (confirmed ? of(void 0) : EMPTY))
          )
      : of(void 0);

    conflictedGuard$
      .pipe(
        tap(() => (this.isCheckingOut = true)),
        switchMap(() =>
          this.orderService.checkoutOrder(this.projectService.currentProjectId, this.order().id).pipe(
            concatMap((form: LiveForm) =>
              from(this.offlineFormsClient.saveOfflineOrder(form, LFOfflineOrderSourceType.USER_SELECTED, false)).pipe(
                switchMap(() => from(this.menuService.buildCommands())),
                tap(() => this.refreshOrders.emit()),
                map(() => form)
              )
            ),
            mergeMap((form: LiveForm) =>
              forkJoin([
                of(form),
                ...form.attachedResources.map((data) =>
                  this.formService.loadFormResource(data.fileId, form.id, this.projectService.currentProjectId)
                )
              ])
            ),
            switchMap(([form, ...blobs]) =>
              forkJoin(
                blobs.map((blob, index) =>
                  this.offlineFormService.upload({
                    ...form.attachedResources[index],
                    data: blob,
                    saveId: '',
                    state: ResourceState.Unchanged,
                    orderId: form.id
                  })
                )
              )
            ),
            catchError((httpError) => {
              this.isCheckingOut = false;
              this.handleCheckoutError(httpError);
              return of(httpError);
            }),
            take(1)
          )
        )
      )
      .subscribe();
  }

  private handleCheckoutError(httpError: any): void {
    const errorCode = this.errorHandling.getErrorCode(httpError || 'UNKNOWN');
    const message = this.errorHandling.getErrorMessage(httpError);
    const title = this.translate.instant('DIALOGS.WARNING');
    this.dialog
      .open(errorCode === 'ORDEROWNLOCK' ? ConfirmDialogComponent : ErrorDialogComponent, {
        data: { title, message },
        closeOnNavigation: true,
        disableClose: true
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((force) => {
          if (force) {
            // this.loadForm(force);
          } else {
            // this.close();
          }
        })
      )
      .subscribe();
  }

  finishOrder(): void {
    throw new Error('Method not implemented.');
  }
  cancelOrder(): void {
    throw new Error('Method not implemented.');
  }

  showInfo(event: Event): void {
    event.stopImmediatePropagation();
  }

  getLink(): [string, string] {
    return [this.order().isEditAllowed ? 'edit' : 'view', this.order().id];
  }
}
